import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRightIcon,
  BicycleIcon,
  CalendarBlankIcon,
  CheckCircleIcon,
  CloudRainIcon,
  CrosshairIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  SpinnerGapIcon,
  UsersThreeIcon,
  XIcon,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { ShareMenu } from "@/components/ShareMenu";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { buildComparePath, compactDayTicketLabel, MAX_COMPARE_SPOTS } from "@/lib/compareSpots";
import { destinationCoverImage } from "@/lib/destinationCover";
import { parkPath } from "@/lib/routes";
import { useAuth } from "@/context/AuthContext";
import { formatDistanceKm } from "@/lib/units";
import { useUnitPreference } from "@/context/UnitPreferenceContext";
import { sortRidePlanCandidates } from "@/lib/ridePlanSorting";

const guestStorageKey = (shareToken) => `bpf:ride-plan:${shareToken}:guest-token`;

const formatDrive = (minutes) => {
  if (minutes == null) return "Drive time unavailable";
  const rounded = Math.max(0, Math.round(minutes));
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m} min`;
};

const prettyDate = (value) => {
  if (!value) return "Date unavailable";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(new Date(`${value}T12:00:00`));
  } catch {
    return value;
  }
};

const humanize = (value) => String(value || "")
  .toLowerCase()
  .split("_")
  .filter(Boolean)
  .map((part) => part[0].toUpperCase() + part.slice(1))
  .join(" ");

const candidateWeather = (candidate) => {
  const condition = humanize(candidate.weather_condition) || "Weather unavailable";
  const hasTemps = candidate.weather_temp_min_c != null || candidate.weather_temp_max_c != null;
  const temps = hasTemps
    ? [candidate.weather_temp_min_c, candidate.weather_temp_max_c]
      .filter((value) => value != null)
      .map((value) => `${Math.round(value)}°`)
      .join("–")
    : null;
  const rain = candidate.precipitation_probability_pct != null
    ? `${Math.round(candidate.precipitation_probability_pct)}% rain`
    : null;
  return [condition, temps, rain].filter(Boolean).join(" · ");
};

const initials = (name) => String(name || "R")
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join("") || "R";

export default function RidePlanRoom() {
  const navigate = useNavigate();
  const { shareToken } = useParams();
  const { user } = useAuth();
  const { unitSystem } = useUnitPreference();
  const [guestToken, setGuestToken] = useState(() => localStorage.getItem(guestStorageKey(shareToken)) || "");
  const [displayName, setDisplayName] = useState("");
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [originQuery, setOriginQuery] = useState("");
  const [originBusy, setOriginBusy] = useState(false);
  const [originError, setOriginError] = useState("");
  const [originEditing, setOriginEditing] = useState(false);
  const [notes, setNotes] = useState({});
  const [compareSpots, setCompareSpots] = useState([]);
  const [sortMode, setSortMode] = useState("GROUP");

  const loadRoom = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get(`/ride-plans/${shareToken}`, { params: { guest_token: guestToken || undefined } });
      setRoom(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [guestToken, shareToken]);

  useEffect(() => { loadRoom(); }, [loadRoom]);

  useEffect(() => {
    if (user?.name && !displayName) setDisplayName(user.name);
  }, [displayName, user]);

  useEffect(() => {
    if (!room) return undefined;
    const id = setInterval(() => loadRoom({ quiet: true }), 5000);
    return () => clearInterval(id);
  }, [loadRoom, room]);

  const joinGuest = async () => {
    if (!displayName.trim()) return;
    setJoining(true);
    setError("");
    try {
      const response = await api.post("/ride-plans/join-guest", {
        share_token: shareToken,
        display_name: displayName.trim(),
      });
      localStorage.setItem(guestStorageKey(shareToken), response.data.guest_token);
      setGuestToken(response.data.guest_token);
      setRoom(response.data.room);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setJoining(false);
    }
  };

  const vote = async (destinationId, value, note) => {
    try {
      const response = await api.post("/ride-plans/vote", {
        share_token: shareToken,
        guest_token: guestToken || undefined,
        destination_id: destinationId,
        value,
        note,
      });
      setRoom(response.data);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const finalize = async (destinationId, { confirmClose = false } = {}) => {
    if (confirmClose && !window.confirm(translateLabel("Close responses for this ride? Riders will no longer be able to change their answer."))) return;
    try {
      const response = await api.post("/ride-plans/finalize", {
        share_token: shareToken,
        destination_id: destinationId,
      });
      setRoom(response.data);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const saveOrigin = async (origin) => {
    setOriginBusy(true);
    setOriginError("");
    try {
      const response = await api.post("/ride-plans/origin", {
        share_token: shareToken,
        guest_token: guestToken || undefined,
        origin,
      });
      setRoom(response.data);
      setOriginQuery("");
      setOriginEditing(false);
    } catch (requestError) {
      setOriginError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setOriginBusy(false);
    }
  };

  const resolveOrigin = async () => {
    const query = originQuery.trim();
    if (!query) return;
    setOriginBusy(true);
    setOriginError("");
    try {
      const response = await api.get("/geocode", { params: { q: query } });
      const first = response.data?.results?.[0];
      if (!first) {
        setOriginError("No matching place found.");
        setOriginBusy(false);
        return;
      }
      await saveOrigin({ label: first.name || query, lat: first.lat, lng: first.lng });
    } catch (requestError) {
      setOriginError(formatApiErrorDetail(requestError.response?.data?.detail));
      setOriginBusy(false);
    }
  };

  const useCurrentOrigin = () => {
    if (!navigator.geolocation) {
      setOriginError("Your browser does not support location.");
      return;
    }
    setOriginBusy(true);
    setOriginError("");
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const origin = { lat: coords.latitude, lng: coords.longitude };
        let label = "Current location";
        try {
          const { data } = await api.get("/reverse-geocode", { params: origin });
          if (data?.name) label = data.name;
        } catch {
          // Coordinates remain useful if reverse geocoding is unavailable.
        }
        saveOrigin({ label, ...origin });
      },
      () => {
        setOriginError("Location permission was not available.");
        setOriginBusy(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  const toggleCompare = (candidate) => {
    const selected = compareSpots.some((item) => item.destination_id === candidate.destination_id);
    if (selected) {
      setCompareSpots((current) => current.filter((item) => item.destination_id !== candidate.destination_id));
      return;
    }
    if (compareSpots.length >= MAX_COMPARE_SPOTS) {
      setError(`You can compare up to ${MAX_COMPARE_SPOTS} parks.`);
      return;
    }
    setError("");
    setCompareSpots((current) => [...current, candidate]);
  };

  const openComparison = () => {
    if (compareSpots.length < 2) {
      setError(translateLabel("Choose at least 2 parks to compare."));
      return;
    }
    const comparisonOrigin = viewer?.origin || room?.plan?.origin;
    navigate(buildComparePath(
      compareSpots.map((candidate) => ({ id: candidate.destination_id })),
      {
        searchCenter: comparisonOrigin
          ? { lat: comparisonOrigin.lat, lng: comparisonOrigin.lng, name: comparisonOrigin.label }
          : undefined,
        returnTo: window.location.pathname,
      },
    ));
  };

  const viewer = room?.viewer;
  const sortedCandidates = useMemo(
    () => sortRidePlanCandidates(room?.candidates, sortMode, viewer?.origin),
    [room?.candidates, sortMode, viewer?.origin],
  );
  const needsGuestJoin = room && !viewer;
  const selectedCandidate = room?.candidates?.find(
    (candidate) => candidate.destination_id === room?.plan?.selected_destination_id,
  );
  const participantCount = room?.participants?.length || 0;
  const isSingleRide = Boolean(
    room?.candidates?.length === 1
    && room?.plan?.selected_destination_id
    && room.candidates[0].destination_id === room.plan.selected_destination_id,
  );
  const singleCandidate = isSingleRide ? room.candidates[0] : null;
  const topYes = Math.max(0, ...(room?.candidates || []).map((candidate) => candidate.yes || 0));
  const shareUrl = `${window.location.origin}/ride-plan/${shareToken}`;

  const sharePayload = {
    url: shareUrl,
    title: isSingleRide ? `Ride at ${singleCandidate?.name || "Bikeparks Finder"}` : "Choose the ride together",
    text: isSingleRide
      ? `Join our ride at ${singleCandidate?.name || "this bike park"} on ${prettyDate(room.plan.date)}.`
      : "Where should we ride? Vote on the shortlist.",
    analytics: {
      content_type: "ride_plan",
      item_id: shareToken,
      item_name: selectedCandidate?.name || "Group ride",
    },
  };

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white">
      <Navbar scrollsWithPage />
      <main className="mx-auto w-full max-w-[1500px] px-4 pb-24 pt-24 sm:px-6 lg:px-8 md:pt-28">
        {loading ? (
          <div className="flex min-h-[50vh] items-center justify-center">
            <SpinnerGapIcon size={28} className="animate-spin text-[#CCFF00]" />
          </div>
        ) : !room ? (
          <div className="rounded-3xl border border-white/10 bg-[#121214] p-8 text-center">
            <h1 className="font-display text-2xl font-bold">{translateLabel("Ride plan not found")}</h1>
            {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
          </div>
        ) : (
          <>
            <header className="border-b border-white/[0.07] pb-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-3xl">
                  <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#CCFF00]">
                    <UsersThreeIcon size={16} weight="bold" /> {isSingleRide ? "Ride plan" : "Group ride"}
                    <span className="text-zinc-700">·</span>
                    <span className={room.plan.status === "OPEN" ? "text-emerald-300" : "text-[#CCFF00]"}>
                      {isSingleRide
                        ? (room.plan.status === "OPEN" ? "Responses open" : "Responses closed")
                        : (room.plan.status === "OPEN" ? "Voting open" : room.plan.status === "DECIDED" ? "Ride chosen" : "Plan closed")}
                    </span>
                  </div>
                  <h1 className="mt-2 font-display text-4xl font-black uppercase leading-[0.94] tracking-tight sm:text-5xl md:text-6xl">
                    {isSingleRide ? `Ride at ${singleCandidate?.name || "the park"}` : "Choose the ride together"}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-400 sm:text-base">
                    {isSingleRide
                      ? "The park is chosen. Join the ride, set your availability and share the plan with friends."
                      : "Everyone gets the same shortlist, votes independently and can see their own drive time."}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <ParticipantStack participants={room.participants} />
                  <div className="hidden text-right sm:block">
                    <div className="text-sm font-bold text-white">{participantCount} rider{participantCount === 1 ? "" : "s"}</div>
                    <div className="text-[11px] text-zinc-600">{translateLabel("Updates every 5s")}</div>
                  </div>
                  <ShareMenu payload={sharePayload} label={translateLabel("Share plan")} compact />
                  {isSingleRide && viewer?.can_finalize && room.plan.status === "OPEN" && (
                    <button
                      type="button"
                      onClick={() => finalize(singleCandidate.destination_id, { confirmClose: true })}
                      className="rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2 text-xs font-extrabold text-zinc-300 transition-colors hover:border-[#CCFF00]/25 hover:text-[#CCFF00]"
                      data-testid="close-ride-responses"
                    >
                      Close responses
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <PlanChip icon={<CalendarBlankIcon size={14} />} text={prettyDate(room.plan.date)} />
                <PlanChip icon={<MapPinIcon size={14} />} text={translateLabel(room.plan.origin.label)} />
                {!isSingleRide && <PlanChip text={`${formatDrive(room.plan.max_drive_minutes)} max`} />}
                {!isSingleRide && <PlanChip text={room.plan.wants?.length ? room.plan.wants.map(humanize).join(" + ") : "Any trails"} />}
                {!isSingleRide && room.plan.access && <PlanChip text={humanize(room.plan.access)} />}
              </div>

              {selectedCandidate && !isSingleRide && (
                <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#CCFF00]/25 bg-[#CCFF00]/[0.07] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#CCFF00] text-black">
                      <CheckCircleIcon size={20} weight="fill" />
                    </span>
                    <div>
                      <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#CCFF00]">{translateLabel("Chosen for the ride")}</div>
                      <div className="font-display text-lg font-black text-white">{selectedCandidate.name}</div>
                    </div>
                  </div>
                  <Link
                    to={parkPath({ id: selectedCandidate.destination_id })}
                    className="inline-flex items-center gap-1.5 self-start text-sm font-bold text-[#CCFF00] hover:text-[#B8E600] sm:self-auto"
                  >
                    View park <ArrowRightIcon size={14} weight="bold" />
                  </Link>
                </div>
              )}
            </header>

            {needsGuestJoin && (
              <section className="mt-5 flex flex-col gap-4 rounded-2xl border border-[#CCFF00]/25 bg-[#CCFF00]/[0.055] p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-extrabold uppercase tracking-[0.15em] text-[#CCFF00]">{isSingleRide ? "Join the ride" : "Join to vote"}</div>
                  <p className="mt-1 text-sm text-zinc-400">{isSingleRide
                    ? "No account needed. Join to say whether you're going, maybe or can't make it."
                    : "No account needed. Your name is only used inside this ride plan."}</p>
                </div>
                <div className="flex w-full gap-2 sm:max-w-md">
                  <input
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    onKeyDown={(event) => { if (event.key === "Enter") joinGuest(); }}
                    placeholder={translateLabel("Your name")}
                    className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/50"
                  />
                  <button
                    type="button"
                    onClick={joinGuest}
                    disabled={joining || !displayName.trim()}
                    className="rounded-xl bg-[#CCFF00] px-5 py-2.5 text-sm font-extrabold text-black disabled:opacity-45"
                  >
                    {joining ? "Joining…" : "Join"}
                  </button>
                </div>
              </section>
            )}

            {viewer && (
              <section className={`mt-5 rounded-2xl border px-4 py-3 ${
                viewer.origin
                  ? "border-white/[0.08] bg-[#111113]"
                  : "border-amber-300/20 bg-amber-300/[0.055]"
              }`}>
                {!originEditing && viewer.origin ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#CCFF00]/10 text-[#CCFF00]">
                        <MapPinIcon size={17} weight="bold" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-zinc-600">{translateLabel("Your private start point")}</div>
                        <div className="truncate text-sm font-bold text-white">{translateLabel(viewer.origin.label)}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setOriginQuery(viewer.origin.label || "");
                        setOriginEditing(true);
                      }}
                      className="self-start text-xs font-bold text-[#CCFF00] hover:text-[#B8E600] sm:self-auto"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPinIcon size={16} className="text-[#CCFF00]" weight="bold" />
                      <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#CCFF00]">
                        {viewer.origin ? "Change your start point" : "Set your start point"}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-5 text-zinc-500">
                      Used only to calculate your drive time. The exact coordinates are not shown to the group.
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
                      <div className="relative">
                        <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                        <input
                          value={originQuery}
                          onChange={(event) => setOriginQuery(event.target.value)}
                          onKeyDown={(event) => { if (event.key === "Enter") resolveOrigin(); }}
                          placeholder={translateLabel("City or postcode")}
                          className="w-full rounded-xl border border-white/10 bg-black/20 py-2.5 pl-9 pr-3 text-sm outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/40"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={resolveOrigin}
                        disabled={originBusy || !originQuery.trim()}
                        className="rounded-xl bg-[#CCFF00] px-4 py-2.5 text-xs font-extrabold text-black disabled:opacity-40"
                      >
                        Set
                      </button>
                      <button
                        type="button"
                        onClick={useCurrentOrigin}
                        disabled={originBusy}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-xs font-bold text-zinc-300 hover:bg-white/[0.04] disabled:opacity-40"
                      >
                        {originBusy ? <SpinnerGapIcon size={15} className="animate-spin" /> : <CrosshairIcon size={15} />}
                        My location
                      </button>
                    </div>
                    {viewer.origin && (
                      <button
                        type="button"
                        onClick={() => {
                          setOriginQuery("");
                          setOriginEditing(false);
                        }}
                        className="mt-2 text-xs font-bold text-zinc-600 hover:text-white"
                      >
                        Cancel
                      </button>
                    )}
                    {originError && <p className="mt-2 text-xs font-semibold text-red-300">{originError}</p>}
                  </div>
                )}
              </section>
            )}

            {error && <p className="mt-4 rounded-xl bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-300">{error}</p>}

            {isSingleRide ? (
              <SingleRideEvent
                candidate={singleCandidate}
                viewer={viewer}
                notes={notes}
                setNotes={setNotes}
                vote={vote}
                planStatus={room.plan.status}
                unitSystem={unitSystem}
                participants={room.participants}
              />
            ) : (
              <section className="mt-8">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#CCFF00]">{translateLabel("Group shortlist")}</div>
                    <h2 className="mt-1 font-display text-3xl font-black uppercase">
                      Vote for your park
                    </h2>
                    <p className="mt-1 text-xs text-zinc-600">
                      {viewer?.can_vote ? "Tap Yes, Maybe or No on each park." : "Join the room to vote."}
                    </p>
                  </div>
                  <div className="inline-flex self-start rounded-xl border border-white/[0.08] bg-black/20 p-1 lg:self-auto" data-testid="group-sort">
                    <button
                      type="button"
                      onClick={() => setSortMode("GROUP")}
                      className={`rounded-lg px-3 py-1.5 text-[11px] font-bold transition-colors ${sortMode === "GROUP" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}
                    >
                      Group order
                    </button>
                    <button
                      type="button"
                      onClick={() => setSortMode("MINE")}
                      disabled={!viewer?.origin}
                      title={!viewer?.origin ? "Set your private start point to sort by your drive time" : undefined}
                      className={`rounded-lg px-3 py-1.5 text-[11px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${sortMode === "MINE" ? "bg-[#CCFF00] text-black" : "text-zinc-500 hover:text-white"}`}
                    >
                      Closest to me
                    </button>
                  </div>
                </div>

                <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {sortedCandidates.map((candidate) => {
                    const selected = room.plan.selected_destination_id === candidate.destination_id;
                    const leading = room.plan.status === "OPEN" && topYes > 0 && candidate.yes === topYes;

                    return (
                      <CandidateCard
                        key={candidate.destination_id}
                        candidate={candidate}
                        selected={selected}
                        leading={leading}
                        viewer={viewer}
                        notes={notes}
                        setNotes={setNotes}
                        vote={vote}
                        finalize={finalize}
                        planStatus={room.plan.status}
                        unitSystem={unitSystem}
                        compareSelected={compareSpots.some((item) => item.destination_id === candidate.destination_id)}
                        onToggleCompare={toggleCompare}
                      />
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {!isSingleRide && compareSpots.length > 0 && (
          <GroupCompareTray
            spots={compareSpots}
            onRemove={toggleCompare}
            onClear={() => setCompareSpots([])}
            onCompare={openComparison}
          />
        )}
      </main>
    </div>
  );
}

function SingleRideEvent({
  candidate,
  viewer,
  notes,
  setNotes,
  vote,
  planStatus,
  unitSystem,
  participants,
}) {
  const noteValue = notes[candidate.destination_id] ?? candidate.my_note ?? "";
  const attendance = candidate.attendance || [];
  const groups = [
    { value: "YES", label: "Going", count: candidate.yes, tone: "text-[#CCFF00]", empty: "No one yet" },
    { value: "MAYBE", label: "Maybe", count: candidate.maybe, tone: "text-amber-300", empty: "No one yet" },
    { value: "NO", label: "Can't go", count: candidate.no, tone: "text-zinc-400", empty: "No one yet" },
  ];
  const unconfirmed = participants.filter(
    (participant) => !attendance.some((entry) => entry.participant_id === participant.id),
  );
  const price = compactDayTicketLabel(candidate.day_ticket_price) || candidate.day_ticket_price;
  const hasMyDrive = candidate.my_drive_minutes != null;
  const location = [candidate.city, candidate.country].filter(Boolean).join(" · ") || translateLabel("Location unavailable");

  return (
    <section className="mt-8" data-testid="single-ride-event">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.8fr)]">
        <article className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[#121214]">
          <div className="relative h-44 overflow-hidden sm:h-56">
            <img
              src={destinationCoverImage(
                { id: candidate.destination_id, name: candidate.name, image: candidate.image },
                candidate.image,
              )}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-black/10 to-black/20" />
            {price && (
              <span className="absolute right-4 top-4 rounded-full bg-black/70 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                {price}
              </span>
            )}
          </div>

          <div className="p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[#CCFF00]">{translateLabel("Ride destination")}</div>
                <h2 className="mt-1 font-display text-3xl font-black uppercase text-white">{candidate.name}</h2>
                <p className="mt-1 text-sm font-semibold text-zinc-500">{location}</p>
              </div>
              <Link
                to={parkPath({ id: candidate.destination_id })}
                className="inline-flex items-center gap-1.5 self-start text-sm font-bold text-[#CCFF00]"
              >
                View park <ArrowRightIcon size={14} weight="bold" />
              </Link>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <DecisionMetric
                label={translateLabel("Your drive")}
                value={
                  hasMyDrive
                    ? `${formatDrive(candidate.my_drive_minutes)}${candidate.my_drive_km != null ? ` · ${formatDistanceKm(candidate.my_drive_km, unitSystem)}` : ""}`
                    : viewer?.origin
                      ? "Unavailable"
                      : viewer
                        ? "Set your start"
                        : "Join to calculate"
                }
                accent={hasMyDrive}
              />
              <DecisionMetric
                label={translateLabel("Weather")}
                value={candidateWeather(candidate)}
                icon={<CloudRainIcon size={13} />}
              />
            </div>

            {viewer?.can_vote && planStatus === "OPEN" && (
              <>
                <div className="mt-5">
                  <div className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-500">{translateLabel("Are you coming?")}</div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <AttendanceButton active={candidate.my_vote === "YES"} onClick={() => vote(candidate.destination_id, "YES", noteValue)} label={translateLabel("Going")} />
                    <AttendanceButton active={candidate.my_vote === "MAYBE"} onClick={() => vote(candidate.destination_id, "MAYBE", noteValue)} label={translateLabel("Maybe")} />
                    <AttendanceButton active={candidate.my_vote === "NO"} danger onClick={() => vote(candidate.destination_id, "NO", noteValue)} label={translateLabel("Can't go")} />
                  </div>
                </div>

                {(candidate.my_vote || noteValue) && (
                  <div className="mt-3 flex gap-2">
                    <input
                      value={noteValue}
                      onChange={(event) => setNotes((current) => ({ ...current, [candidate.destination_id]: event.target.value }))}
                      placeholder={translateLabel("Optional note…")}
                      maxLength={240}
                      className="min-w-0 flex-1 rounded-xl border border-white/[0.08] bg-black/20 px-3 py-2.5 text-sm outline-none placeholder:text-zinc-700 focus:border-[#CCFF00]/35"
                    />
                    <button
                      type="button"
                      disabled={!candidate.my_vote}
                      onClick={() => vote(candidate.destination_id, candidate.my_vote, noteValue)}
                      className="rounded-xl border border-white/[0.08] px-3 py-2.5 text-xs font-bold text-zinc-300 disabled:opacity-30"
                    >
                      Save
                    </button>
                  </div>
                )}
              </>
            )}

            {candidate.notes?.length > 0 && (
              <div className="mt-4 border-t border-white/[0.06] pt-3">
                <div className="text-[10px] font-black uppercase tracking-[0.14em] text-zinc-600">{translateLabel("Ride notes")}</div>
                <div className="mt-2 space-y-2">
                  {candidate.notes.map((item) => (
                    <p key={item.participant_id} className="text-xs leading-5 text-zinc-400">
                      <span className="font-bold text-zinc-200">{item.display_name}</span>
                      <span className="text-zinc-700"> · </span>
                      {item.note}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        <aside className="rounded-3xl border border-white/[0.08] bg-[#121214] p-4 sm:p-5">
          <div className="label-tiny text-[#CCFF00]">{translateLabel("Attendance")}</div>
          <h2 className="mt-1 font-display text-2xl font-black uppercase">{translateLabel("Who's riding?")}</h2>

          <div className="mt-4 space-y-4">
            {groups.map((group) => {
              const people = attendance.filter((entry) => entry.value === group.value);
              return (
                <div key={group.value}>
                  <div className="flex items-center justify-between">
                    <div className={`text-xs font-extrabold uppercase tracking-[0.13em] ${group.tone}`}>{translateLabel(group.label)}</div>
                    <div className="text-xs font-bold text-zinc-500">{group.count}</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {people.length ? people.map((person) => (
                      <span key={person.participant_id} className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.035] px-2.5 py-1.5 text-xs font-semibold text-zinc-300">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-800 text-[8px] font-black">{initials(person.display_name)}</span>
                        {person.display_name}
                      </span>
                    )) : <span className="text-xs text-zinc-600">{group.empty}</span>}
                  </div>
                </div>
              );
            })}

            {unconfirmed.length > 0 && (
              <div className="border-t border-white/[0.06] pt-4">
                <div className="text-xs font-extrabold uppercase tracking-[0.13em] text-zinc-600">Not answered · {unconfirmed.length}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {unconfirmed.map((person) => (
                    <span key={person.id} className="rounded-full border border-white/[0.06] px-2.5 py-1.5 text-xs text-zinc-500">
                      {person.display_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

function AttendanceButton({ active, danger = false, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-3 py-3 text-xs font-extrabold transition-colors ${
        active
          ? danger
            ? "border-red-400/50 bg-red-500/15 text-red-200"
            : "border-[#CCFF00]/50 bg-[#CCFF00]/12 text-[#CCFF00]"
          : "border-white/[0.08] bg-white/[0.035] text-zinc-400 hover:bg-white/[0.06]"
      }`}
    >
      {label}
    </button>
  );
}

function GroupCompareTray({ spots, onRemove, onClear, onCompare }) {
  return (
    <div className="pointer-events-none fixed inset-x-3 bottom-[calc(var(--app-footer-height)+0.75rem)] z-40 flex justify-center sm:bottom-5">
      <div className="pointer-events-auto flex w-full max-w-3xl items-center gap-3 rounded-2xl border border-white/10 bg-[#0b0b0d]/95 px-3 py-2.5 shadow-[0_18px_60px_rgba(0,0,0,0.42)] backdrop-blur-xl sm:w-auto sm:min-w-[34rem]">
        <div className="flex shrink-0 items-baseline gap-2">
          <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#CCFF00]">{translateLabel("Compare")}</span>
          <span className="text-[11px] font-semibold text-zinc-500">{spots.length}/{MAX_COMPARE_SPOTS}</span>
        </div>

        <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto bpf-scrollbar-hidden">
          {spots.map((park) => (
            <button
              key={park.destination_id}
              type="button"
              onClick={() => onRemove(park)}
              title={`Remove ${park.name}`}
              className="inline-flex max-w-[11rem] shrink-0 items-center gap-1.5 rounded-lg border border-white/[0.07] bg-white/[0.045] px-2.5 py-1.5 text-[11px] font-semibold text-zinc-300 transition-colors hover:border-white/15 hover:bg-white/[0.08]"
            >
              <span className="truncate">{park.name}</span>
              <XIcon size={11} className="shrink-0 text-zinc-500" />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onClear}
          className="hidden shrink-0 px-1.5 py-1 text-[11px] font-bold text-zinc-500 transition-colors hover:text-white sm:block"
        >
          {translateLabel("Clear")}
        </button>
        <button
          type="button"
          onClick={onCompare}
          disabled={spots.length < 2}
          className="shrink-0 rounded-xl bg-[#CCFF00] px-3.5 py-2 text-xs font-extrabold text-black transition-all hover:bg-[#B8E600] disabled:cursor-not-allowed disabled:opacity-35"
        >
          {translateLabel("Compare")}
        </button>
      </div>
    </div>
  );
}

function ParticipantStack({ participants = [] }) {
  const visible = participants.slice(0, 4);
  const extra = Math.max(0, participants.length - visible.length);

  return (
    <div className="flex -space-x-2">
      {visible.map((participant) => (
        <div
          key={participant.id}
          title={`${participant.display_name}${participant.role === "HOST" ? " · Host" : ""}`}
          className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#0b0b0d] text-[10px] font-black ${
            participant.role === "HOST"
              ? "bg-[#CCFF00] text-black"
              : "bg-zinc-800 text-zinc-200"
          }`}
        >
          {initials(participant.display_name)}
        </div>
      ))}
      {extra > 0 && (
        <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#0b0b0d] bg-zinc-900 text-[10px] font-black text-zinc-400">
          +{extra}
        </div>
      )}
    </div>
  );
}

function PlanChip({ icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.035] px-3 py-1.5 text-xs font-semibold text-zinc-400">
      {icon}{text}
    </span>
  );
}

function CandidateCard({
  candidate,
  selected,
  leading,
  viewer,
  notes,
  setNotes,
  vote,
  finalize,
  planStatus,
  unitSystem,
  compareSelected,
  onToggleCompare,
}) {
  const price = compactDayTicketLabel(candidate.day_ticket_price) || candidate.day_ticket_price;
  const noteValue = notes[candidate.destination_id] ?? candidate.my_note ?? "";
  const hasMyDrive = candidate.my_drive_minutes != null;
  const location = [candidate.city, candidate.country].filter(Boolean).join(" · ") || "Location unavailable";
  const showPlanOrigin = candidate.drive_minutes != null && (
    candidate.my_drive_minutes == null
    || Math.abs(candidate.drive_minutes - candidate.my_drive_minutes) > 1
    || (
      candidate.drive_km != null
      && candidate.my_drive_km != null
      && Math.abs(candidate.drive_km - candidate.my_drive_km) > 1
    )
  );

  return (
    <article
      className={`overflow-hidden rounded-2xl border bg-[#121214] transition-colors ${
        selected
          ? "border-[#CCFF00]/60 shadow-[0_0_0_1px_rgba(204,255,0,0.14)]"
          : planStatus === "DECIDED"
            ? "border-white/[0.06] opacity-70"
            : "border-white/[0.08]"
      }`}
    >
      <div className="relative h-24 overflow-hidden">
        <img
          src={destinationCoverImage(
            { id: candidate.destination_id, name: candidate.name, image: candidate.image },
            candidate.image,
          )}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/20 to-black/20" />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {selected && (
            <span className="rounded-full bg-[#CCFF00] px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-black">
              Chosen
            </span>
          )}
          {leading && !selected && (
            <span className="rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#CCFF00] backdrop-blur">
              Most yes votes
            </span>
          )}
        </div>
        {price && (
          <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur">
            {price}
          </span>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-black leading-tight text-white">{candidate.name}</h3>
            <p className="mt-1 truncate text-xs font-semibold text-zinc-600">{location}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <Link
              to={parkPath({ id: candidate.destination_id })}
              className="text-xs font-bold text-zinc-400 transition-colors hover:text-[#CCFF00]"
            >
              View park
            </Link>
            <button
              type="button"
              onClick={() => onToggleCompare(candidate)}
              aria-pressed={compareSelected}
              className={`rounded-md border px-2 py-1 text-[9px] font-extrabold uppercase tracking-wide transition-colors ${
                compareSelected
                  ? "border-[#CCFF00]/40 bg-[#CCFF00] text-black"
                  : "border-white/[0.08] text-zinc-600 hover:border-[#CCFF00]/25 hover:text-[#CCFF00]"
              }`}
            >
              {compareSelected ? "Compared" : "+ Compare"}
            </button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <DecisionMetric
            label={translateLabel("Your drive")}
            value={
              hasMyDrive
                ? `${formatDrive(candidate.my_drive_minutes)}${candidate.my_drive_km != null ? ` · ${formatDistanceKm(candidate.my_drive_km, unitSystem)}` : ""}`
                : viewer?.origin
                  ? "Unavailable"
                  : viewer
                    ? "Set your start"
                    : "Join to calculate"
            }
            accent={hasMyDrive}
          />
          <DecisionMetric
            label={translateLabel("Weather")}
            value={candidateWeather(candidate)}
            icon={<CloudRainIcon size={13} />}
          />
        </div>

        {showPlanOrigin && (
          <div className="mt-1.5 text-[10px] text-zinc-600">
            {translateLabel("Plan origin")}: {formatDrive(candidate.drive_minutes)}
            {candidate.drive_km != null ? ` · ${formatDistanceKm(candidate.drive_km, unitSystem)}` : ""}
          </div>
        )}

        <div className="mt-3 grid grid-cols-3 gap-1.5">
          <VoteButton
            active={candidate.my_vote === "YES"}
            disabled={!viewer?.can_vote || planStatus !== "OPEN"}
            onClick={() => vote(candidate.destination_id, "YES", noteValue)}
            label={translateLabel("Yes")}
            count={candidate.yes}
            emoji="👍"
          />
          <VoteButton
            active={candidate.my_vote === "MAYBE"}
            disabled={!viewer?.can_vote || planStatus !== "OPEN"}
            onClick={() => vote(candidate.destination_id, "MAYBE", noteValue)}
            label={translateLabel("Maybe")}
            count={candidate.maybe}
            emoji="🤔"
          />
          <VoteButton
            active={candidate.my_vote === "NO"}
            danger
            disabled={!viewer?.can_vote || planStatus !== "OPEN"}
            onClick={() => vote(candidate.destination_id, "NO", noteValue)}
            label={translateLabel("No")}
            count={candidate.no}
            emoji="👎"
          />
        </div>

        {viewer?.can_vote && planStatus === "OPEN" && (candidate.my_vote || noteValue) && (
          <div className="mt-2 flex gap-1.5">
            <input
              value={noteValue}
              onChange={(event) => setNotes((current) => ({ ...current, [candidate.destination_id]: event.target.value }))}
              placeholder={translateLabel("Add a note for the group…")}
              maxLength={240}
              className="min-w-0 flex-1 rounded-lg border border-white/[0.08] bg-black/20 px-2.5 py-2 text-xs outline-none placeholder:text-zinc-700 focus:border-[#CCFF00]/35"
            />
            <button
              type="button"
              disabled={!candidate.my_vote}
              onClick={() => vote(candidate.destination_id, candidate.my_vote, noteValue)}
              className="rounded-lg border border-white/[0.08] px-2.5 py-2 text-[11px] font-bold text-zinc-400 transition-colors hover:text-white disabled:opacity-30"
            >
              Save
            </button>
          </div>
        )}

        {candidate.notes?.length > 0 && (
          <div className="mt-2 space-y-1.5 border-t border-white/[0.06] pt-2">
            {candidate.notes.map((item) => (
              <p key={item.participant_id} className="text-[11px] leading-4 text-zinc-400">
                <span className="font-bold text-zinc-200">{item.display_name}</span>
                <span className="text-zinc-700"> · </span>
                {item.note}
              </p>
            ))}
          </div>
        )}

        {viewer?.can_finalize && planStatus === "OPEN" && (
          <button
            type="button"
            onClick={() => finalize(candidate.destination_id)}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#CCFF00]/25 bg-[#CCFF00]/[0.06] px-3 py-2 text-xs font-extrabold text-[#CCFF00] transition-colors hover:bg-[#CCFF00]/10"
          >
            Choose park & close voting <ArrowRightIcon size={14} weight="bold" />
          </button>
        )}
      </div>
    </article>
  );
}

function DecisionMetric({ label, value, accent = false, icon = null }) {
  return (
    <div className="rounded-lg bg-white/[0.035] p-2.5">
      <div className="flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.12em] text-zinc-600">
        {icon}{label}
      </div>
      <div className={`mt-1 text-xs font-bold leading-5 ${accent ? "text-[#CCFF00]" : "text-zinc-300"}`}>
        {value}
      </div>
    </div>
  );
}

function VoteButton({ active, danger, disabled, onClick, label, count, emoji }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center justify-center gap-1 rounded-lg border px-2 py-2 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        active
          ? danger
            ? "border-red-400/50 bg-red-500/15 text-red-200"
            : "border-[#CCFF00]/50 bg-[#CCFF00]/12 text-[#CCFF00]"
          : "border-white/[0.08] bg-white/[0.035] text-zinc-400 hover:bg-white/[0.06]"
      }`}
    >
      <span className="text-xs">{emoji}</span>
      <span className="text-[10px] font-extrabold uppercase tracking-wide">{label} · {count}</span>
    </button>
  );
}
